import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { ArrowRight, BadgeCheck, CalendarClock, HandCoins, Wrench } from "lucide-react";
import Seo from "@/components/Seo";
import { STATES } from "@/data/states";
import { trackEvent } from "@/lib/analytics";
import { SubpageShell } from "@/components/landing/SubpageShell";
import { CheckoutModal } from "@/components/landing/CheckoutModal";
import { Reveal } from "@/components/landing/Reveal";

const STEPS = [
    {
        title: "Get your rate in under a minute",
        copy: "Your price is built on your vehicle's year, make, model, and mileage — not a one-size contract with a giant price tag.",
    },
    {
        title: "Pick a payment rhythm",
        copy: "Pay monthly, quarterly, or annually. Coverage works like the subscriptions you already manage — cancel anytime.",
    },
    {
        title: "Hand it to your service advisor",
        copy: "When something breaks, your dealership's service counter handles the claim. Approved repairs are paid at the dealer's retail rate.",
    },
];

const PERKS = [
    {
        icon: BadgeCheck,
        title: "Accepted at the counter",
        copy: "AdvantaShield was created by dealership service directors, so it's honored at franchise service departments instead of being turned away.",
    },
    {
        icon: HandCoins,
        title: "No big upfront price",
        copy: "Subscription pricing means protection that fits a monthly budget — not a lump sum rolled into a loan.",
    },
    {
        icon: Wrench,
        title: "Claims without the hold music",
        copy: "Claims are filed and paid online, so your advisor spends minutes on approval — not hours on the phone.",
    },
    {
        icon: CalendarClock,
        title: "Cancel anytime",
        copy: "No locked-in multi-year terms. Stay covered for exactly as long as the coverage earns its keep.",
    },
];

export default function StatePage() {
    const { slug } = useParams();
    const state = STATES.find((s) => s.slug === slug);
    const [checkoutOpen, setCheckoutOpen] = useState(false);

    useEffect(() => {
        trackEvent("page_view");
    }, [slug]);

    if (!state) return <Navigate to="/" replace />;

    const [c1, c2, c3] = state.cities;
    const openQuote = () => {
        trackEvent("quote_open");
        setCheckoutOpen(true);
    };

    const faqs = [
        {
            q: `Is AdvantaShield available in ${state.name}?`,
            a: `Yes. AdvantaShield covers eligible vehicles in ${state.name} and all 50 states. Most vehicles up to 15 model years old or 150,000 miles qualify, with coverage extending up to 185,000 miles.`,
        },
        {
            q: `Can I take my car to my own dealership in ${state.name}?`,
            a: "That's the point. AdvantaShield was built by dealership service directors to be accepted at the franchise service counter — the same place you already trust for maintenance and repairs.",
        },
        {
            q: `How much does coverage cost in ${state.name}?`,
            a: "Your rate depends on your vehicle's year, make, model, and mileage, and whether you pay monthly, quarterly, or annually. A custom quote takes under a minute, with no robocalls and no pressure.",
        },
    ];

    return (
        <SubpageShell onQuote={openQuote}>
            <Seo
                title={`Extended Car Warranty in ${state.name} | AdvantaShield`}
                siteName="AdvantaShield"
                description={`Dealer-built vehicle service contracts for ${state.name} drivers. Subscription pricing, accepted at dealership service counters across ${state.name}. Get a quote in under a minute.`}
                image="/og-image.png"
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        itemListElement: [
                            { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
                            { "@type": "ListItem", position: 2, name: "Coverage by State", item: `${window.location.origin}/coverage` },
                            { "@type": "ListItem", position: 3, name: state.name },
                        ],
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        serviceType: "Extended Vehicle Service Contract",
                        name: `AdvantaShield Vehicle Service Contract in ${state.name}`,
                        provider: { "@type": "Organization", name: "AdvantaShield" },
                        areaServed: { "@type": "State", name: state.name },
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        mainEntity: faqs.map((f) => ({
                            "@type": "Question",
                            name: f.q,
                            acceptedAnswer: { "@type": "Answer", text: f.a },
                        })),
                    },
                ]}
            />

            <Reveal>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-sky-400">
                    Vehicle Service Contracts — {state.name}
                </p>
                <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl">
                    Extended car warranty coverage{" "}
                    <span className="text-sky-400">in {state.name}.</span>
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg">
                    From {c1} to {c2} and {c3}, {state.name} drivers are switching to
                    coverage built by the people who actually fix cars. AdvantaShield
                    is the vehicle service contract created by dealership service
                    directors — accepted at the service counter, priced like a
                    subscription, and never a large upfront payment.
                </p>
                <div className="mt-9 flex flex-wrap items-center gap-4">
                    <button
                        data-testid="state-quote-btn"
                        onClick={openQuote}
                        className="group inline-flex items-center gap-2.5 rounded-full bg-sky-400 px-7 py-4 text-base font-bold text-[#0A0D14] transition-[background-color,box-shadow,transform] duration-300 hover:bg-sky-300 hover:shadow-[0_0_44px_rgba(56,189,248,0.5)] active:scale-95"
                    >
                        Get My {state.name} Quote
                        <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                    <p className="text-sm text-slate-500">
                        Under a minute. No robocalls. Ever.
                    </p>
                </div>
            </Reveal>

            <Reveal delay={0.1}>
                <h2 className="mt-20 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                    How coverage works in {state.name}
                </h2>
                <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
                    {STEPS.map((s, i) => (
                        <div
                            key={s.title}
                            data-testid={`state-step-${i}`}
                            className="rounded-2xl border border-white/10 bg-[#121722] p-6"
                        >
                            <p className="font-mono text-sm font-medium text-sky-400">
                                / 0{i + 1}
                            </p>
                            <h3 className="mt-3 font-display text-lg font-semibold text-white">
                                {s.title}
                            </h3>
                            <p className="mt-2 text-sm leading-relaxed text-slate-400">
                                {s.copy}
                            </p>
                        </div>
                    ))}
                </div>
            </Reveal>

            <Reveal delay={0.1}>
                <h2 className="mt-20 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                    Why {state.name} drivers switch to AdvantaShield
                </h2>
                <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {PERKS.map((p, i) => (
                        <div
                            key={p.title}
                            data-testid={`state-perk-${i}`}
                            className="flex items-start gap-4 rounded-2xl border border-white/10 bg-[#121722] p-6"
                        >
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-400/10 text-sky-400">
                                <p.icon className="h-5 w-5" />
                            </span>
                            <div>
                                <h3 className="font-display text-base font-semibold text-white">
                                    {p.title}
                                </h3>
                                <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
                                    {p.copy}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </Reveal>

            <Reveal delay={0.1}>
                <h2 className="mt-20 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                    {state.name} coverage questions
                </h2>
                <div className="mt-6 flex flex-col divide-y divide-white/10">
                    {faqs.map((f, i) => (
                        <div key={f.q} data-testid={`state-faq-${i}`} className="py-6">
                            <h3 className="font-display text-base font-semibold text-white md:text-lg">
                                {f.q}
                            </h3>
                            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-400">
                                {f.a}
                            </p>
                        </div>
                    ))}
                </div>
            </Reveal>

            <Reveal delay={0.1}>
                <div className="mt-16 rounded-3xl border border-sky-400/20 bg-[#0C1019] p-8 md:p-12">
                    <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                        Ready to cover your car in{" "}
                        <span className="text-sky-400">{state.name}?</span>
                    </h2>
                    <p className="mt-4 max-w-xl text-base text-slate-400">
                        Join {state.name} drivers who pay for protection like a
                        subscription — and get it honored at the service counter.
                    </p>
                    <button
                        data-testid="state-quote-btn-bottom"
                        onClick={openQuote}
                        className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-sky-400 px-7 py-4 text-base font-bold text-[#0A0D14] transition-[background-color,box-shadow,transform] duration-300 hover:bg-sky-300 hover:shadow-[0_0_44px_rgba(56,189,248,0.5)] active:scale-95"
                    >
                        Get My Instant Quote
                        <ArrowRight className="h-5 w-5" />
                    </button>
                </div>
                <p className="mt-12 text-sm text-slate-500">
                    AdvantaShield serves drivers in all 50 states, including{" "}
                    {["texas", "california", "florida", "arizona"]
                        .filter((s) => s !== state.slug)
                        .slice(0, 3)
                        .map((s) => {
                            const st = STATES.find((x) => x.slug === s);
                            return (
                                <a
                                    key={s}
                                    href={`/coverage/${s}`}
                                    className="text-sky-400 transition-colors hover:text-sky-300"
                                >
                                    {st.name}
                                </a>
                            );
                        })
                        .reduce((acc, el, i) => (i === 0 ? [el] : [...acc, ", ", el]), [])}
                    .{" "}
                    <a href="/" className="text-sky-400 transition-colors hover:text-sky-300">
                        Learn more about the program
                    </a>
                    .
                </p>
            </Reveal>

            <CheckoutModal open={checkoutOpen} onOpenChange={setCheckoutOpen} />
        </SubpageShell>
    );
}
